<template>
  <v-row v-if="workflowObject" class="ma-0 h-100">
    <split-view>
      <template #left>
        <v-list v-model:selected="tabs">
          <domain-file-list-item
            :item="{ name: '__template', path: '__template', displayName: 'Code Generation Steps' }"
            :domainObjects="domainObjects"
          />
        </v-list>
        <v-divider />
        <v-list
          height="774"
          bg-color="transparent"
          density="compact"
          open-strategy="multiple"
          v-model:selected="tabs"
          v-model:opened="opened"
        >
          <domain-file-list-item
            v-for="item in flattenedItems"
            :key="item.path"
            :item="item"
            :domainObjects="domainObjects"
            :isTemplateSelected="!!selectedTemplate"
            :readModelMergeInfo="item.path === 'domain-model/readmodel' ? readModelMergeInfo : null"
            @click="toggle(item)"
          />
        </v-list>
      </template>
      <template #right>
        <div
          v-if="openedTab === '__template'"
          class="d-flex flex-column h-100"
          style="position: relative;"
        >
          <div v-if="openApiSpec" class="d-flex justify-space-between align-center mb-2">
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  size="small"
                  style="border-radius: 0 !important;"
                  variant="text"
                  density="compact"
                  class="text-none"
                  @click="openApiSpec = !openApiSpec"
                >
                  <v-icon class="mr-2">mdi-arrow-left</v-icon>
                  Back To Code Generation Steps
                </v-btn>
              </template>
              <span>View API Specification</span>
            </v-tooltip>
          </div>
          <div v-if="openApiSpec">
            <code-diff-viewer
              v-if="previousSwaggerSpec !== currentSwaggerSpec"
              :key="'diff-viewer-' + previousSwaggerSpec.length"
              :original="previousSwaggerSpec"
              :modelValue="currentSwaggerSpec"
              :isDirty="false"
              :readOnly="true"
              language="yaml"
            />
            <code-editor
              v-else
              :modelValue="previousSwaggerSpec"
              :isDirty="false"
              :readOnly="true"
              language="yaml"
            />
          </div>
          <div v-else class="d-flex flex-column pa-4 h-100" style="max-width: 800px;">
            <div class="mb-2">
              <div class="d-flex align-center ga-2">
                <v-icon v-if="isValidationValid" color="green">mdi-checkbox-marked-outline</v-icon>
                <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
                <p class="text-h6">
                  1. Validate Domain Model
                </p>
                <v-chip
                  v-if="hasValidationIssues"
                  :color="validationErrors.length > 0 ? 'error' : (validationWarnings.length > 0 ? 'warning' : 'info')"
                  size="small"
                  variant="flat"
                  class="ml-2"
                  @click="validationExpanded = !validationExpanded"
                  style="cursor: pointer;"
                >
                  {{ validationSummary }}
                  <v-icon end size="small">
                    {{ validationExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </v-chip>
                <v-chip
                  v-else
                  color="success"
                  size="small"
                  variant="tonal"
                  class="ml-2"
                >
                  <v-icon start size="small">mdi-check</v-icon>
                  Valid
                </v-chip>
              </div>
              <v-expand-transition>
                <div v-if="validationExpanded && hasValidationIssues" class="mt-3 ml-8">
                  <div v-if="validationErrors.length > 0" class="mb-3">
                    <div class="text-subtitle-2 text-error mb-1 d-flex align-center ga-1">
                      <v-icon size="small" color="error">mdi-alert-circle</v-icon>
                      Major Issues
                    </div>
                    <v-list density="compact" class="validation-list bg-transparent pa-0">
                      <v-list-item
                        v-for="error in validationErrors"
                        :key="error.id + error.code"
                        class="validation-item px-2 rounded"
                        @click="navigateToIssue(error)"
                      >
                        <template #prepend>
                          <v-icon :icon="getValidationIcon(error.type)" size="small" color="error" />
                        </template>
                        <v-list-item-title class="text-body-2">
                          {{ error.message }}
                        </v-list-item-title>
                        <template #append>
                          <v-icon size="small" color="grey">mdi-arrow-right</v-icon>
                        </template>
                      </v-list-item>
                    </v-list>
                  </div>
                  <div v-if="validationWarnings.length > 0" class="mb-3">
                    <div class="text-subtitle-2 text-warning mb-1 d-flex align-center ga-1">
                      <v-icon size="small" color="warning">mdi-alert</v-icon>
                      Minor Issues
                    </div>
                    <v-list density="compact" class="validation-list bg-transparent pa-0">
                      <v-list-item
                        v-for="warning in validationWarnings"
                        :key="warning.id + warning.code + (warning.field || '')"
                        class="validation-item px-2 rounded"
                        @click="navigateToIssue(warning)"
                      >
                        <template #prepend>
                          <v-icon :icon="getValidationIcon(warning.type)" size="small" color="warning" />
                        </template>
                        <v-list-item-title class="text-body-2">
                          {{ warning.message }}
                        </v-list-item-title>
                        <template #append>
                          <v-icon size="small" color="grey">mdi-arrow-right</v-icon>
                        </template>
                      </v-list-item>
                    </v-list>
                  </div>
                  <div v-if="validationNotices.length > 0">
                    <div class="text-subtitle-2 text-info mb-1 d-flex align-center ga-1">
                      <v-icon size="small" color="info">mdi-information-outline</v-icon>
                      Notices
                    </div>
                    <v-list density="compact" class="validation-list bg-transparent pa-0">
                      <v-list-item
                        v-for="notice in validationNotices"
                        :key="notice.id + notice.code + (notice.field || '')"
                        class="validation-item px-2 rounded"
                        @click="navigateToIssue(notice)"
                      >
                        <template #prepend>
                          <v-icon :icon="getValidationIcon(notice.type)" size="small" color="info" />
                        </template>
                        <v-list-item-title class="text-body-2">
                          {{ notice.message }}
                        </v-list-item-title>
                        <template #append>
                          <v-icon size="small" color="grey">mdi-arrow-right</v-icon>
                        </template>
                      </v-list-item>
                    </v-list>
                  </div>
                </div>
              </v-expand-transition>
            </div>

            <v-divider class="my-4" />

            <!-- Step 2: Choose Framework (was Step 1) -->
            <div class="d-flex align-center ga-2 mb-2">
              <v-icon v-if="!!selectedTemplate" color="green">mdi-checkbox-marked-outline</v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <p class="text-h6">
                2. Choose a framework boilerplate and get coding in seconds.
              </p>
            </div>

            <div class="mb-2">
              <v-menu
                v-model="menuOpen"
                :close-on-content-click="false"
                location="bottom start"
                offset="5"
              >
                <template #activator="{ props }">
                  <v-list
                    v-bind="props"
                    elevation="2"
                    rounded
                    class="template-activator-list"
                  >
                    <v-list-item
                      v-if="selectedTemplate"
                      :key="selectedTemplate.name"
                      density="comfortable"
                      class="template-activator-item"
                    >
                      <template #prepend>
                        <v-avatar :color="selectedTemplate.color" size="36">
                          <v-icon :icon="selectedTemplate.icon" color="white" />
                        </v-avatar>
                      </template>

                      <v-list-item-title class="font-weight-medium">
                        {{ selectedTemplate.name }}
                        {{ selectedTemplate.isLegacy && selectedRepo?.name ? `- ${selectedRepo.name}` : '' }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="mb-1">
                        {{ selectedTemplate.description }}
                        {{ selectedTemplate.isLegacy && selectedRepo?.language ? `Language - ${selectedRepo.language}.` : '' }}
                      </v-list-item-subtitle>

                      <template #append>
                        <v-icon>mdi-chevron-down</v-icon>
                      </template>
                    </v-list-item>

                    <v-list-item
                      v-else
                      density="comfortable"
                      class="template-activator-placeholder"
                    >
                      <v-list-item-title class="text-grey-darken-1">
                        Select a framework...
                      </v-list-item-title>
                      <template #append>
                        <v-icon>mdi-chevron-down</v-icon>
                      </template>
                    </v-list-item>
                  </v-list>
                </template>

                <v-list
                  elevation="2"
                  rounded
                  class="framework-menu-list"
                  style="max-height: 420px;"
                >
                  <v-list-item
                    v-for="(template, index) in templates"
                    :key="index"
                    @click="selectTemplate(template)"
                    density="comfortable"
                    :disabled="template.disabled"
                    :class="[
                      'template-item',
                      template.name === selectedTemplate?.name
                        ? 'selected'
                        : 'hoverable'
                    ]"
                  >
                    <template #prepend>
                      <v-avatar :color="template.color" size="36">
                        <v-icon :icon="template.icon" color="white" />
                      </v-avatar>
                    </template>

                    <v-list-item-title class="font-weight-medium">
                      {{ template.name }}
                      {{ template.isLegacy && selectedRepo?.name ? `- ${selectedRepo.name}` : '' }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="mb-1">
                      {{ template.description }}
                      {{ template.isLegacy && selectedRepo?.language ? `Language - ${selectedRepo.language}.` : '' }}
                    </v-list-item-subtitle>
                    <template #append>
                      <v-btn
                        v-if="!template.disabled && template.repo"
                        :href="template.repo"
                        target="_blank"
                        rel="noopener"
                        icon
                        variant="text"
                        color="white"
                        @click.stop
                      >
                        <v-icon :color="template.name === selectedTemplate?.name ? 'white' : 'blue-grey'">
                          mdi-open-in-new
                        </v-icon>
                      </v-btn>
                      <span v-else-if="!template.isLegacy">
                        Coming Soon...
                      </span>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="reviewedApiSpec" color="green">mdi-checkbox-marked-outline</v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <p class="text-h6">
                3. Review the
              </p>
              <v-tooltip
                location="top"
                :text="hasRootEntity
                  ? 'To complete this step, click to inspect the OpenAPI / Swagger specification.'
                  : 'In order to view the API specification, connect at least one entity to the bounded context.'
                "
              >
                <template v-slot:activator="{ props }">
                  <div v-bind="props">
                    <v-btn
                      v-bind="props"
                      variant="text"
                      density="compact"
                      class="api-spec-btn"
                      :active="openApiSpec"
                      @click="onApiSpecClick"
                      :disabled="!hasRootEntity"
                    >
                      <span v-if="previousSwaggerSpec !== currentSwaggerSpec" class="api-change-indicator" />
                      API Specification
                    </v-btn>
                  </div>
                </template>
              </v-tooltip>
              <v-menu>
                <template #activator="{ on, props }">
                  <v-btn
                    v-bind="props"
                    variant="text"
                    size="small"
                    icon="mds:more_vert"
                    v-on="{ ...on }"
                    @click.prevent
                  />
                </template>

                <v-list density="compact">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <div  v-bind="props">
                        <v-list-item
                          @click="handlePushSwagger"
                          prepend-icon="mdi-github"
                          title="Simplified alternative - only push specification"
                          :disabled="!selectedRepo || isSwaggerSynced"
                        />
                      </div>
                    </template>
                    <span>{{ pushSpecsTooltipText }}</span>
                  </v-tooltip>
                </v-list>
              </v-menu>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="codeGenerated" color="green">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <div class="d-flex align-center ga-2">
                <p class="text-h6">
                  4. Generate code with AI
                </p>
                <div class="d-flex align-center">
                  <v-tooltip
                    location="top"
                    text="To complete this step, click to generate a backend with commands, entities, tests, queries, and/or a frontend application."
                  >
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-btn
                          class="generate-api-btn"
                          @click="handleGenerate"
                          :disabled="store.state.isWorkflowDisabled || !selectedTemplate || (!includeBackend && !includeFrontend)"
                        >
                          <v-icon v-if="!isGenerating" start>mdi-brain</v-icon>
                          <v-icon v-else start>mdi-stop</v-icon>
                          {{ generateBtnLabel }}
                        </v-btn>
                      </div>
                    </template>
                  </v-tooltip>

                  <v-tooltip
                    location="top"
                    text="Generate a backend app in the server folder"
                  >
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-checkbox
                          v-if="!selectedTemplate?.isLegacy"
                          v-model="includeBackend"
                          label="Include Backend"
                          density="compact"
                          color="green"
                          hide-details
                          class="ml-4"
                          :disabled="store.state.isWorkflowDisabled || !selectedTemplate"
                        />
                      </div>
                    </template>
                  </v-tooltip>

                  <v-tooltip
                    location="top"
                    text="Generate a single-page app in the client folder"
                  >
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-checkbox
                          v-if="!selectedTemplate?.isLegacy"
                          v-model="includeFrontend"
                          label="Include Frontend"
                          density="compact"
                          color="green"
                          hide-details
                          class="ml-4"
                          :disabled="store.state.isWorkflowDisabled || !selectedTemplate"
                        />
                      </div>
                    </template>
                  </v-tooltip>
                </div>
              </div>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="isApproved" color="green">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <div class="d-flex align-center ga-2">
                <p class="text-h6 pr-3">
                  5. Save changes
                </p>
                <v-tooltip
                  location="top"
                  max-width="640"
                  text="To complete this step, click here to approve all pending changes. Alternatively,
                   approve individual files or folders in the file tree (Domain tab or Code tab) by selecting
                   'Apply patch' wherever yellow dots indicate changes."
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-btn class="generate-api-btn" :disabled="store.state.isWorkflowDisabled || allFilesApproved" @click="approveAllChanges">
                        <v-icon start>mdi-content-save-all</v-icon>
                        <span>Approve all changes</span>
                      </v-btn>
                    </div>
                  </template>
                </v-tooltip>
                <v-divider vertical />
                <v-tooltip
                  location="top"
                  text="Discard all unapproved file changes (files with yellow dots in the file tree)."
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-btn class="generate-api-btn" :disabled="store.state.isWorkflowDisabled || allFilesApproved" @click="discardAllChanges">
                        <v-icon start>mdi-content-save-off</v-icon>
                        <span>Discard all changes</span>
                      </v-btn>
                    </div>
                  </template>
                </v-tooltip>
              </div>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="testsCompleted" color="green">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <div class="d-flex align-center ga-2">
                <p class="text-h6">
                  6. Run Tests
                </p>
                <v-tooltip
                  location="top"
                  text="To complete this step, click to run tests and verify your code works correctly."
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-btn
                        @click="openRunTestsDialog = !openRunTestsDialog"
                        class="generate-api-btn"
                      >
                        <v-icon start>mdi-test-tube</v-icon>
                        Run Tests
                      </v-btn>
                    </div>
                  </template>
                </v-tooltip>
              </div>
            </div>

            <v-divider class="my-4" />
            <div class="d-flex align-center ga-2">
              <v-icon v-if="isCommittedAndPushed" color="green">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
              <v-tooltip location="top">
                <template v-slot:activator="{ props: props }">
                  <p class="text-h6" v-bind="props">
                    7. Commit and push to GitHub
                  </p>
                </template>
                <span>Navigate to the GitHub tab to commit and push your changes.</span>
              </v-tooltip>
            </div>

            <v-spacer></v-spacer>
            <div class="mt-5">
              <div class="d-flex justify-space-between">
                <div class="toolbar-absolute-bottom d-flex align-center overflow-hidden pb-1">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: props }">
                      <v-btn
                        v-bind="props"
                        @click="openConfirmResetProject = true"
                        class="generate-api-btn mr-4"
                        :disabled="store.state.isWorkflowDisabled || (!selectedTemplate && !boundedContext.githubRepo)"
                      >
                        <v-icon start>mdi-broom</v-icon>
                        Reset Code View
                      </v-btn>
                    </template>
                    <span>Unlink the GitHub repository and delete all generated code files in Continuum Design (files on GitHub will not be affected).</span>
                  </v-tooltip>
                </div>
                <div>
                  <ai-version-selector
                    style="max-width: 232px;"
                    :modelValue="selectedAiModel"
                    @update:modelValue="(val) => emit('update:selectedAiModel', val)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <template v-else>
          <template v-for="(object, filename) in domainObjects" :key="filename">
            <div v-if="openedTab === filename && filename !== 'domain-model'" style="position: relative;">
              <workflow-aggregates-code-viewer
                v-if="object.filepath"
                :object="object"
                @saveFile="saveDomainCode"
                @deleteFile="onFileDelete(object)"
                @update:object="(obj) => handleObjectUpdate(obj, filename)"
              />
            </div>
          </template>
        </template>
      </template>
    </split-view>
    <v-dialog v-model="askBeforeUpdateFramework" max-width="400">
      <v-card title="Warning">
        <v-card-text>
          You already have an existing project. Changing the framework will <b>permanently delete</b> all current
          code files and replace them with the selected template.
          <br><br>
          Are you sure you want to continue?
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="cancelFrameworkUpdate">Cancel</v-btn>
          <v-btn variant="flat" color="primary" size="large" @click="onUpdateFramework">Continue</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="openConfirmResetProject" max-width="450">
      <v-card title="Confirm Project Reset">
        <v-card-text class="py-4">
          <p class="mb-4">You are about to perform an irreversible action.</p>
          <p>
            This will <b>permanently delete</b> all generated code files within this bounded context and unlink the associated GitHub repository.
          </p>
          <br>
          <p>Are you sure you want to proceed?</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" variant="text" @click="openConfirmResetProject = false">
            Cancel
          </v-btn>
          <v-btn variant="flat" color="error" size="large" @click="handleConfirmResetProject">
            Reset Project
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <PreviewPromptDialog
      v-model="openPreviewPrompt"
      :content="previewPromptContent"
      :cardDescription="previewCardDescription"
      @update:modelValue="onTogglePreviewDialog"
    />
    <RunTestsDialog
      v-model="openRunTestsDialog"
      :allFiles="allFiles"
      @update:modelValue="onToggleRunTestsDialog"
      @test-results="onTestResults"
    />
    <v-dialog v-model="showDiffDialog" max-width="1000" max-height="800">
      <v-card title="View Domain Changes">
        <code-diff-viewer
          :original="JSON.stringify(viewingDiff?.old || {}, null, 2)"
          :modelValue="JSON.stringify(viewingDiff?.new || {}, null, 2)"
          :isDirty="false"
          :readOnly="true"
          :wordWrap="true"
          language="json"
        />

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="() => showDiffDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script setup>
import _ from 'lodash'
import { useStore } from 'vuex'
// import { useRoute } from 'vue-router'
import { computed, ref, watch, provide, nextTick } from 'vue'
import SplitView from '@/components/SplitView.vue'
import CodeEditor from '@/components/CodeEditor.vue'
import DomainFileListItem from '@/components/DomainFileListItem.vue'
import CodeDiffViewer from '@/components/CodeDiffViewer.vue'
import AiVersionSelector from '@/components/AiVersionSelector.vue'
import PreviewPromptDialog from '@/components/PreviewPromptDialog.vue'
import RunTestsDialog from '@/components/RunTestsDialog.vue'
import WorkflowAggregatesCodeViewer from '@/components/WorkflowAggregatesCodeViewer.vue'
import { collapsePaths, flattenTree } from '@/codegen/utils'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { convertWorkflowToSwaggerYaml } from '@/codegen/apiSpecificationBuilder'
import { SchemaType } from '@/constants/schema'
import { DomainModelRole } from '@/constants/cardTypes'
import {
  DOMAIN_MODEL_STRUCTURE,
  CODE_EDITOR_APPLY_PATCH,
  CODE_EDITOR_GENERATE_AGAIN,
  CODE_EDITOR_SHOW_DOMAIN_CHANGES,
  CODE_EDITOR_EDIT_PROMPT,
  CODE_EDITOR_COPY_PROMPT
} from '@/codegen/constants'
import { getValidationIcon } from '@/utils/domainModelValidator'

const {
  isGenerating,
  isAborted,
  workflowObject,
  fileTree,
  fileObjects,
  selectedAiModel,
  templates,
  selectedTemplate,
  boundedContext,
  selectedRepo,
  isCommitted,
  isSwaggerSynced,
  currentWorkflow,
  getDomainItemFinalPrompt,
  detectDomainChanges,
  validationErrors,
  validationWarnings,
  validationNotices
} = defineProps({
  isGenerating: {
    type: Boolean,
    default: false
  },
  isAborted: {
    type: Boolean,
    default: false
  },
  workflowObject: {
    type: Object
  },
  fileTree: {
    type: Object
  },
  fileObjects: {
    type: Object
  },
  selectedAiModel: {
    type: Number,
    default: 4
  },
  templates: {
    type: Array
  },
  selectedTemplate: {
    type: Object,
    default: null
  },
  boundedContext: {
    type: Object
  },
  selectedRepo: {
    type: Object
  },
  isCommitted: {
    type: Boolean,
    default: false
  },
  isSwaggerSynced: {
    type: Boolean,
    default: false
  },
  currentWorkflow: {
    type: Object
  },
  getDomainItemFinalPrompt: {
    type: Function
  },
  detectDomainChanges: {
    type: Function
  },
  validationErrors: {
    type: Array,
    default: () => []
  },
  validationWarnings: {
    type: Array,
    default: () => []
  },
  validationNotices: {
    type: Array,
    default: () => []
  }
})
const emit = defineEmits(
  [
    'updateFramework',
    'update:object',
    'saveFile',
    'deleteFile',
    'update:selectedAiModel',
    'updateApi',
    'clearApi',
    'pushSwaggerToGithub',
    'abortUpdate',
    'navigateToDomainView'
  ])

const tabs = ref(['__template'])
const opened = ref([
  'domain-model',
  'domain-model/entity',
  'domain-model/command',
  'domain-model/readmodel',
  'domain-model/gwt'
])
const openedTab = ref('__template')
const pendingTemplate = ref(null)
const askBeforeUpdateFramework = ref(false)
const openApiSpec = ref(false)
const domainTree = ref({})
const defaultTree = ref({})
const collapseDomainTree = ref({})
const domainObjects = ref({})
const dirtyFolders = ref(new Set())
const specReviewed = ref(false)
const menuOpen = ref(false)
const includeBackend = ref(true)
const includeFrontend = ref(false)
const openConfirmResetProject = ref(false)
const openPreviewPrompt = ref(false)
const previewPromptContent = ref('')
const previewCardDescription = ref('')
const openRunTestsDialog = ref(false)
const testResults = ref(null) // null = not run, { success: true/false, timestamp: ... } = completed
const showDiffDialog = ref(false)
const viewingDiff = ref(null)
const localIsAborted = ref(false)
const validationExpanded = ref(false)

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const mapCardType = (t) => {
  if (!t) {
    return undefined
  }

  switch (t.domainModelRole) {
    case DomainModelRole.COMMAND:
      return 'command'
    case DomainModelRole.READ_MODEL:
      return 'readmodel'
    case DomainModelRole.GIVEN_WHEN_THEN:
      return 'gwt'
    default:
      return undefined
  }
}
const domainModelData = computed(() => {
  if (!workflow.value || !boundedContext) {
    return { cards: [], readModelMergeInfo: null }
  }
  const contextEntities = workflow.value.schemas?.filter((model) => {
    return model.type === SchemaType.ENTITY && model.boundedContext === boundedContext.id
  }) || []
  const allEntityIds = new Set(workflow.value.schemas?.map((e) => e.id))
  const contextEntityIds = new Set(contextEntities.map((e) => e.id))

  const contextEvents = (workflow.value.domainEvents || []).filter((event) => {
    if (!event.cards) {
      return false
    }

    const entityCard = event.cards.find((rt) => rt.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
    return entityCard && contextEntityIds.has(entityCard.schemaId)
  })

  const allCards = _.flatMap(contextEvents, (event) =>
    event.cards.map((card) => {
      let entityId
      let cardinality
      if (card.cardType?.domainModelRole === DomainModelRole.READ_MODEL && card.schemaId) {
        const querySchema = workflow.value.schemas?.find((s) => s.id === card.schemaId)
        entityId = querySchema?.entityId
        cardinality = querySchema?.cardinality || 'one-to-many'
      } else if (card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT) {
        entityId = card.schemaId
      } else if (card.cardType?.domainModelRole === DomainModelRole.COMMAND) {
        const entityCard = event.cards.find((c) => c.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
        entityId = entityCard?.schemaId
      }
      return {
        id: card.id,
        description: card.description,
        cardType: mapCardType(card.cardType),
        entityId,
        cardinality
      }
    })
  )

  const [readModelCards, otherCards] = _.partition(allCards, (card) => card.cardType === 'readmodel')
  const readModelsWithEntity = readModelCards.filter((rm) => rm.entityId && allEntityIds.has(rm.entityId))

  const groupedReadModels = _.groupBy(
    readModelsWithEntity,
    (rm) => `${rm.entityId}-${rm.cardinality}`
  )

  const relatedEntities = []
  const mergedReadModelCards = Object.values(groupedReadModels).map((group) => {
    const firstCard = group[0]
    const allIds = group.map((c) => c.id).sort()
    const entity = workflow.value.schemas.find((e) => e.id === firstCard.entityId)
    relatedEntities.push(entity)
    return {
      id: firstCard.id,
      ids: allIds,
      description: firstCard.cardinality === 'one-to-many' ? `Get all ${entity.name}s` : `Get ${entity.name} by ID`,
      cardType: 'readmodel'
    }
  })

  const originalCount = readModelsWithEntity.length
  const mergedCount = mergedReadModelCards.length
  let mergeInfo = null

  if (originalCount > mergedCount) {
    mergeInfo = {
      original: originalCount,
      merged: mergedCount
    }
  }

  const schemasMap = new Map((workflow.value.schemas || []).map((e) => [e.id, e]))
  const relatedEntitiesSet = new Set()

  for (const contextEntity of contextEntities) {
    for (const field of contextEntity.fields) {
      if (field.relatedEntityId) {
        const entity = schemasMap.get(field.relatedEntityId)
        if (entity && !relatedEntitiesSet.has(entity.id)) {
          relatedEntitiesSet.add(entity.id)
          relatedEntities.push(entity)
        }
      }
    }
  }

  const availableEntities = [...contextEntities, ...relatedEntities].map((model) => ({
    id: model.id,
    description: model.name,
    cardType: 'entity'
  }))

  const finalCards = _.flatten([
    otherCards.map(({ id, description, cardType }) => ({ id, description, cardType })),
    mergedReadModelCards,
    availableEntities
  ])

  return {
    cards: finalCards,
    readModelMergeInfo: mergeInfo
  }
})
const eventCards = computed(() => domainModelData.value.cards)
const readModelMergeInfo = computed(() => domainModelData.value.readModelMergeInfo)

const flattenedItems = computed(() =>
  flattenTree(collapseDomainTree.value || {}, opened.value, '', 0)
)

const pushSpecsTooltipText = computed(() => {
  if (!selectedRepo) {
    return 'Navigate to the GitHub tab and connect a repository to push the API specification file.'
  }

  return isSwaggerSynced
    ? 'Swagger file is up to date with the repository.'
    : 'The specification file will be saved to server/src/docs/swagger.yaml.'
})

const allFiles = computed(() => {
  return Object.values(fileObjects || {}).filter((file) =>
    file.filepath !== '__workflow' && !file.filepath.includes('.qlerify/'))
})
const allFilesApproved = computed(() => {
  return allFiles.value.every((file) => !file.isDirty && !file.draftContents)
})
const previousSwaggerSpec = computed(() => {
  const parsedWorkflow = workflowObject ? JSON.parse(workflowObject.contents) : { schemas: [], domainEvents: [] }
  const entities = (parsedWorkflow.schemas || []).filter((schema) => schema.type === SchemaType.ENTITY)

  return convertWorkflowToSwaggerYaml({ ...parsedWorkflow, entities }, boundedContext)
})
const currentSwaggerSpec = computed(() => {
  if (!workflow.value || !boundedContext) {
    return ''
  }
  return convertWorkflowToSwaggerYaml(currentWorkflow, boundedContext)
})
const reviewedApiSpec = computed(() => {
  return !!selectedTemplate && (specReviewed.value || codeGenerated.value)
})
const codeGenerated = computed(() => {
  const hasFiles = !!allFiles.value.length
  const hasGeneratedDomainItems = !!Object.keys(domainObjects.value).filter((t) => t.split('/').length > 3).length
  const hasPendingDomainChanges = Object.values(domainObjects.value).some((obj) => obj.isDomainDirty)

  return hasFiles && hasGeneratedDomainItems && !hasPendingDomainChanges
})
const isApproved = computed(() => {
  return reviewedApiSpec.value &&
    codeGenerated.value &&
    Object.keys(fileObjects).length > 1 &&
    !!allFilesApproved.value &&
    Object.keys(domainObjects.value).filter((t) => t.split('/').length > 3).length
})
const testsCompleted = computed(() => {
  // Check if tests have been run and completed successfully
  return testResults.value?.success === true
})

const isCommittedAndPushed = computed(() => {
  return reviewedApiSpec.value && codeGenerated.value && isApproved.value && isCommitted
})

const hasRootEntity = computed(() => {
  return boundedContext.systems && Array.isArray(boundedContext.systems) && !!boundedContext.systems[0]?.groupedBy.rootEntity
})

const isValidationValid = computed(() => {
  return validationErrors.length === 0
})

const hasValidationIssues = computed(() => {
  return validationErrors.length > 0 || validationWarnings.length > 0 || validationNotices.length > 0
})

const validationSummary = computed(() => {
  const parts = []
  if (validationErrors.length > 0) {
    parts.push(`${validationErrors.length} major`)
  }
  if (validationWarnings.length > 0) {
    parts.push(`${validationWarnings.length} minor`)
  }
  if (validationNotices.length > 0) {
    parts.push(`${validationNotices.length} ${validationNotices.length === 1 ? 'notice' : 'notices'}`)
  }
  return parts.join(' \u2022 ')
})

const navigateToIssue = (issue) => {
  emit('navigateToDomainView', issue)
}

const generateBtnLabel = computed(() => {
  if (isGenerating) {
    return 'Stop Generating'
  }

  if (!codeGenerated.value) {
    return previousSwaggerSpec.value === currentSwaggerSpec.value
      ? 'Regenerate API'
      : 'Generate API'
  }

  return 'Update API'
})

const selectTemplate = (template) => {
  if (selectedTemplate?.name === template.name) {
    return
  }

  const projectExists = !!allFiles.value.length

  if (projectExists) {
    askBeforeUpdateFramework.value = true
    pendingTemplate.value = template
  } else {
    emit('updateFramework', template)
  }

  menuOpen.value = false
}

const onUpdateFramework = () => {
  if (pendingTemplate.value) {
    emit('updateFramework', pendingTemplate.value)
  }
  askBeforeUpdateFramework.value = false
  pendingTemplate.value = null
}

const handleObjectUpdate = (object) => {
  const updatedObject = { ...fileObjects[object.realpath], isDirty: true, tags: object.tags }
  emit('update:object', updatedObject)
  updateDomain(fileObjects)
}

const cancelFrameworkUpdate = () => {
  askBeforeUpdateFramework.value = false
  pendingTemplate.value = null
}

const onApiSpecClick = () => {
  openApiSpec.value = true
  specReviewed.value = true
}

const handleGenerate = () => {
  if (!isGenerating) {
    localIsAborted.value = false
    emit('updateApi', includeBackend.value, includeFrontend.value)
  } else {
    localIsAborted.value = true
    emit('abortUpdate')
  }
}

const handleConfirmResetProject = () => {
  openConfirmResetProject.value = false
  emit('clearApi')
}

const handlePushSwagger = () => {
  emit('pushSwaggerToGithub')
}

const collapseObject = (obj, maxDepth = 3, currentDepth = 0) => {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (currentDepth >= maxDepth) {
    // Collapse deeper structures
    return collapsePaths(obj)
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => collapseObject(item, maxDepth, currentDepth + 1))
  }

  const collapsed = {}
  for (const [key, value] of Object.entries(obj)) {
    collapsed[key] = collapseObject(value, maxDepth, currentDepth + 1)
  }
  return collapsed
}

const updateDomain = (objects) => {
  const templates = {
    'domain-model/entity': 'root/.qlerify/entity-template.md',
    'domain-model/command': 'root/.qlerify/command-template.md',
    'domain-model/readmodel': 'root/.qlerify/readmodel-template.md',
    'domain-model/gwt': 'root/.qlerify/gwt-template.md'
  }
  const types = ['entity', 'command', 'readmodel', 'gwt']
  const totalDomainChanges = new Set()
  const totalDiffs = {}
  const isEntityReady = !detectDomainChanges('entity').length
  for (const type of types) {
    const domainItems = Object.keys(defaultTree.value['domain-model'][type])
    for (const item of domainItems) {
      templates[`domain-model/${type}/${item}`] = `root/.qlerify/${type}-templates/${type}-${item}.md`
    }
    const diffs = detectDomainChanges(type) || []
    for (const diff of diffs) {
      const target = diff.new || diff.old
      const normalizedDescription = (target.name || target.description)
        .toLowerCase()
        .replace(/\s+/g, '-')
      totalDomainChanges.add(`root/.qlerify/${type}-templates/${type}-${normalizedDescription}.md`)
      totalDiffs[`root/.qlerify/${type}-templates/${type}-${normalizedDescription}.md`] = { ...diff, domainType: type }
    }
    if (diffs.length) {
      totalDomainChanges.add(`domain-model/${type}`)
    }
  }

  domainObjects.value = Object.fromEntries(
    Object.entries(templates).map(([key, path]) => {
      const { filepath, ...rest } = objects[path] ?? {}
      return [key, { ...rest, filepath, realpath: filepath, isDomainDirty: totalDomainChanges.has(key) || totalDomainChanges.has(filepath), isEntityReady, diff: totalDiffs[filepath] }]
    })
  )
  domainObjects.value['domain-model'] = { isDomainDirty: !!totalDomainChanges.size }
  dirtyFolders.value = new Set()

  const domainPathPerTag = {
    entity: 'domain-model/entity/',
    readmodel: 'domain-model/readmodel/',
    command: 'domain-model/command/',
    gwt: 'domain-model/gwt/'
  }
  const tempPaths = {
    'domain-model/entity/': {},
    'domain-model/readmodel/': {},
    'domain-model/command/': {},
    'domain-model/gwt/': {}
  }

  const validCardIds = new Set(eventCards.value.map((card) => card.id))

  Object.entries(domainPathPerTag).forEach(([tag, basePath]) => {
    Object.entries(objects).forEach(([path, object]) => {
      const tags = object.tags || []
      const typeTags = tags.map((tag) => {
        const [type, id] = tag.split(/-(.+)/)
        return { type, id }
      })
      let cardIds = []
      for (const { type, id } of typeTags) {
        if (type === tag) {
          cardIds.push(id)
        }
      }
      cardIds = cardIds.filter((cardId) => validCardIds.has(cardId))
      if (cardIds?.length) {
        for (const cardId of cardIds) {
          const cardDescription = eventCards.value.find((card) => card.id === cardId)?.description || ''
          // const fullPath = _.kebabCase(sanitizeCardDescription(cardDescription)) + '/' + object.filepath.replace('root/server/src/', '')
          const fullPath = sanitizeCardDescription(cardDescription).toLowerCase().replace(/\s+/g, '-') + '/' + object.filepath.replace('root/server/src/', '')
          domainObjects.value[basePath + fullPath] = { ...object, filepath: fullPath, realpath: object.filepath, cardDescription }

          if (object.draftContents) {
            const parts = (basePath + fullPath).split('/')
            let path = ''

            for (let i = 0; i < parts.length; i++) {
              path = i === 0 ? parts[i] : `${path}/${parts[i]}`
              dirtyFolders.value.add(path)
            }
          }

          const parts = fullPath.split('/')
          let curr = tempPaths[basePath]
          parts.forEach((part) => {
            if (!curr[part]) {
              curr[part] = {}
            }
            curr = curr[part]
          })
        }
      }
    })
  })

  domainTree.value = _.merge({}, defaultTree.value, {
    'domain-model': {
      entity: tempPaths['domain-model/entity/'],
      readmodel: tempPaths['domain-model/readmodel/'],
      command: tempPaths['domain-model/command/'],
      gwt: tempPaths['domain-model/gwt/']
    }
  })

  collapseDomainTree.value = collapseObject(domainTree.value)
}

const toggle = (item) => {
  const index = opened.value.indexOf(item.path)
  if (index >= 0) {
    opened.value.splice(index, 1)
  } else {
    opened.value.push(item.path)
  }
}

provide('treeAction', async (data) => {
  const files = Object.entries(domainObjects.value)
    .filter(([key]) => {
      const parts = key.split('/')
      return parts.length > 3 && key.startsWith(data.path)
    })
    .map(([_, value]) => value)
  switch (data.type) {
    case CODE_EDITOR_APPLY_PATCH:
      for (const file of files) {
        const tempFile = _.cloneDeep(fileObjects[file.realpath])
        tempFile.contents = tempFile.draftContents
        tempFile.draftContents = null
        emit('saveFile', tempFile)
      }
      break
    case CODE_EDITOR_GENERATE_AGAIN:
      const pathParts = data.path.split('/')

      localIsAborted.value = false

      if (pathParts.length === 1) {
        emit('updateApi', true, false)
      } else {
        // Extract domain IDs from selected items
        const domainItems = Object.entries(domainObjects.value)
          .filter(([key]) => {
            const parts = key.split('/')
            return parts.length === 3 && key.startsWith(data.path)
          })
          .map(([_, value]) => value)

        const specificDomainIds = []
        for (const item of domainItems) {
          const parts = item.filepath.split('/')
          const itemInfo = parts[parts.length - 1].replace('.md', '')
          const itemInfoParts = itemInfo.split('-')
          const type = itemInfoParts[0]

          // Get domain ID
          const searchKey = itemInfoParts.slice(1).join('-').toLowerCase()
          const domainCard = eventCards.value.find((card) => {
            if (card.cardType !== type) {
              return false
            }

            const descKey = card.description
              ? sanitizeCardDescription(card.description).toLowerCase().replace(/\s+/g, '-')
              : ''

            return searchKey === descKey
          })

          if (domainCard) {
            const domainId = domainCard.ids || domainCard.id
            if (Array.isArray(domainId)) {
              specificDomainIds.push(...domainId)
            } else {
              specificDomainIds.push(domainId)
            }
          }
        }

        emit('updateApi', true, false, specificDomainIds)
      }
      break
    case CODE_EDITOR_SHOW_DOMAIN_CHANGES:
      const domainType = data.path.split('/')[1]
      const typeChanges = detectDomainChanges(domainType)
      viewingDiff.value = typeChanges.find((diff) => {
        const target = diff.old || diff.new
        return (target.name || target.description || '').toLowerCase().replace(/\s+/g, '-') === data.name
      })
      if (viewingDiff.value) {
        showDiffDialog.value = true
      }
      break
    case CODE_EDITOR_EDIT_PROMPT:
      openedTab.value = data.path
      break
    case CODE_EDITOR_COPY_PROMPT:
      const normalizedPath = data.path.split('/').slice(1, 3).join('-')
        .toLowerCase()
        .replace(/\s+/g, '-')
      const type = data.path.split('/')[1]

      const promptPath = `root/.qlerify/${type}-templates/${normalizedPath}.md`
      const prompt = getDomainItemFinalPrompt(fileObjects[promptPath])
      previewPromptContent.value = prompt
      openPreviewPrompt.value = true
      const desc = files[0]?.cardDescription
      previewCardDescription.value = desc ? `${_.capitalize(type)}: ${desc}` : ''
      break
  }
})

const onTogglePreviewDialog = (val) => {
  if (!val) {
    previewPromptContent.value = ''
    previewCardDescription.value = ''
  }
}

const onToggleRunTestsDialog = (val) => {
  // Handle dialog state changes if needed
}

const onTestResults = (results) => {
  // Update test results when tests complete
  testResults.value = results
}

const approveAllChanges = () => {
  const fileredKeys = Object.keys(fileObjects)
    .filter((key) => fileObjects[key]?.draftContents)

  for (const key of fileredKeys) {
    const tempFile = _.cloneDeep(fileObjects[key])
    tempFile.contents = tempFile.draftContents
    tempFile.draftContents = null
    emit('saveFile', tempFile)
  }
}

const discardAllChanges = () => {
  const fileredKeys = Object.keys(fileObjects)
    .filter((key) => fileObjects[key]?.draftContents)

  for (const key of fileredKeys) {
    const tempFile = _.cloneDeep(fileObjects[key])

    if (tempFile.contents) {
      tempFile.draftContents = null
      emit('saveFile', tempFile)
    } else {
      emit('deleteFile', tempFile)
    }
  }
}

const saveDomainCode = (object) => {
  const transformedObject = _.cloneDeep(object)
  transformedObject.filepath = transformedObject.realpath
  delete transformedObject.realpath
  emit('saveFile', transformedObject)
}

const onFileDelete = (object) => {
  emit('deleteFile', object)
}

watch(() => tabs.value, (newVal) => {
  if (newVal[0]) {
    openedTab.value = newVal[0]
  } else {
    tabs.value[0] = openedTab.value
  }
})

watch([() => fileObjects, () => fileTree], async () => {
  await nextTick()
  updateDomain(fileObjects)
}, { deep: true })

watch(() => eventCards.value, (cards) => {
  const defaultDomainTree = _.cloneDeep(DOMAIN_MODEL_STRUCTURE)
  const domainCards = cards.filter((card) => card.cardType)

  for (const card of domainCards) {
    const description = sanitizeCardDescription(card.description)
      .toLowerCase()
      .replace(/\s+/g, '-')

    defaultDomainTree['domain-model'][card.cardType][description] = {}
  }

  defaultTree.value = _.cloneDeep(defaultDomainTree)
  domainTree.value = _.cloneDeep(defaultDomainTree)

  updateDomain(fileObjects)
}, { immediate: true })

watch(() => isAborted, (val) => {
  localIsAborted.value = val
})
</script>

<style scoped>
.api-change-indicator {
  position: absolute;
  top: 8px;
  right: 4px;
  width: 8px;
  height: 8px;
  background-color: #ff5252;
  border-radius: 50%;
  box-shadow: 0 0 0 rgba(255, 82, 82, 0.7);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 82, 82, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
  }
}

.template-activator-list {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 1px solid var(--light-4);
}

.template-activator-list:hover {
  border-color: var(--dark-1);
  background-color: var(--light-1);
}

.framework-menu-list {
  background-color: var(--light-2) !important;
}

.template-item {
  transition: background-color 0.2s, color 0.2s;
}

.template-item.hoverable {
  background-color: var(--light-2) !important;
}

.template-item.hoverable:hover {
  background-color: var(--light-3) !important;
  color: var(--text-dark);
}

.template-item.selected {
  background-color: #616161 !important;
  color: white;
}

.template-item.selected:hover {
  background-color: #546E7A !important;
  color: white;
}

.template-item {
  &.v-list-item--disabled {
    opacity: 0.4;
  }
}

.api-spec-btn {
  height: 36px !important;
  padding-left: 8px;
}

[data-theme="dark"] .api-spec-btn:not(.v-btn--disabled),
[data-theme="dark"] .generate-api-btn:not(.v-btn--disabled) {
  border: 1px solid var(--light-4);
}

.validation-list {
  max-height: 200px;
  overflow-y: auto;
}

.validation-item {
  min-height: 32px !important;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-items: flex-start;
}

.validation-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.validation-item :deep(.v-list-item-title) {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  -webkit-line-clamp: unset;
  line-height: 1.35;
  padding-block: 4px;
}

.validation-item :deep(.v-list-item__prepend),
.validation-item :deep(.v-list-item__append) {
  align-self: flex-start;
  padding-top: 4px;
}
</style>
